import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { IReduxRequestNode } from "@litres-libs/types";
import { Request } from "../../request/Request";
import { addHydrateReducer } from "../../utils/hydrateReducer";

export interface IUserActionsState {
  updateUserDataCat2: TUpdateUserDataCat2;
  updateMyRegCSRF: TUpdateMyRegCSRF;
  uniteUsersCSRF: TUniteUsersCSRF;
}

type TUpdateUserDataCat2 = IReduxRequestNode<
  unknown,
  IUpdateUserDataResponseCat2
>;
type TUpdateMyRegCSRF = IReduxRequestNode<
  IUpdateMyRegCSRFRequest,
  IUpdateMyRegCSRFResponse
>;
type TUniteUsersCSRF = IReduxRequestNode<
  IUniteUsersCSRFRequest,
  IUniteUsersCSRFRequest
>;

const initialState: IUserActionsState = {
  updateUserDataCat2: {},
  updateMyRegCSRF: {},
  uniteUsersCSRF: {},
};

export interface IUpdateUserDataResponseCat2 {
  success: boolean;
}

export const updateUserDataCat2 = createAsyncThunk(
  "user/actions/updateUserDataCat2",
  // if you type your function argument here
  async (params: {
    data: unknown;
    onSuccess?: (success: TUpdateUserDataCat2) => void;
    onError?: (error: TUpdateUserDataCat2) => void;
  }) => {
    const response = new Request.Cat2<unknown, IUpdateUserDataResponseCat2>({
      url: "/",
      provider: {
        id: "w_update_user_data",
      },
      request: {
        method: "POST",
        params: params.data,
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

export interface IUpdateMyRegCSRFParams {
  mail?: string;
  phone?: string;
  tcountry?: string;
}
export interface IUpdateMyRegCSRFRequest extends IUpdateMyRegCSRFParams {
  action: string;
  ajax: number;
  save_email: number;
  skip_email_check: number;
  gu_ajax: boolean;
}
export interface IUpdateMyRegCSRFResponse {
  success: boolean;
  error_msg?: string;
}

export const updateMyRegCSRF = createAsyncThunk(
  "user/actions/updateMyRegCSRF",
  // if you type your function argument here
  async (params: {
    data: IUpdateMyRegCSRFParams;
    onSuccess?: (success: TUpdateMyRegCSRF) => void;
    onError?: (error: TUpdateMyRegCSRF) => void;
  }) => {
    const response = new Request.CSRF<
      IUpdateMyRegCSRFRequest,
      IUpdateMyRegCSRFResponse
    >({
      url: "/",
      provider: {
        id: "UPDATE_MY_REG_GUJ",
      },
      request: {
        method: "POST",
        data: {
          action: "update_my_reg",
          ajax: 1,
          save_email: 1,
          skip_email_check: 1,
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

export interface IUniteUsersCSRFRequest {
  action: string;
  id: "all";
  gu_ajax: boolean;
}
export interface IUpdateUserDataResponseCat2 {
  success: boolean;
}

export const uniteUsersCSRF = createAsyncThunk(
  "user/actions/uniteUsersCSRF",
  // if you type your function argument here
  async (params?: {
    onSuccess?: (success: TUpdateUserDataCat2) => void;
    onError?: (error: TUpdateUserDataCat2) => void;
  }) => {
    const response = new Request.CSRF<
      IUniteUsersCSRFRequest,
      IUniteUsersCSRFRequest
    >({
      url: "/",
      provider: {
        id: "UNITE_USERS_CSRF",
      },
      request: {
        method: "POST",
        data: {
          action: "unite_users",
          id: "all",
          gu_ajax: true,
        },
      },
      onSuccess: params?.onSuccess,
      onError: params?.onError,
    });

    return await response.fetch();
  }
);

export const userActionsSlice = createSlice({
  name: "user/actions",
  initialState,
  reducers: {
    // Сеттеров нет
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "user.actions");
    /**
     * Обновление данныз юзера ЛК на кат2
     */
    builder.addCase(updateUserDataCat2.pending, (state: IUserActionsState) => {
      state.updateUserDataCat2.isLoading = true;
      state.updateUserDataCat2.error = undefined;
    });
    builder.addCase(
      updateUserDataCat2.fulfilled,
      (
        state: IUserActionsState,
        action: PayloadAction<TUpdateUserDataCat2>
      ) => {
        state.updateUserDataCat2.isLoading = false;
        state.updateUserDataCat2 = action.payload;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error(
            "Ошибка запроса updateUserDataCat2",
            action.payload.error
          );
          state.updateUserDataCat2.error = action.payload.error;
        }
      }
    );

    /**
     * Обновление данныз юзера на CSRF, используется в авторизациях и регистрациях
     */
    builder.addCase(updateMyRegCSRF.pending, (state: IUserActionsState) => {
      state.updateMyRegCSRF.isLoading = true;
      state.updateMyRegCSRF.error = undefined;
    });
    builder.addCase(
      updateMyRegCSRF.fulfilled,
      (state: IUserActionsState, action: PayloadAction<TUpdateMyRegCSRF>) => {
        state.updateMyRegCSRF.isLoading = false;
        state.updateMyRegCSRF = action.payload;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error("updateMyRegCSRF", action.payload.error);
          state.updateMyRegCSRF.error = action.payload.error;
        }
      }
    );

    /**
     * Ручка вызывающая мерж юзеров
     */
    builder.addCase(uniteUsersCSRF.pending, (state: IUserActionsState) => {
      state.uniteUsersCSRF.isLoading = true;
      state.uniteUsersCSRF.error = undefined;
    });
    builder.addCase(
      uniteUsersCSRF.fulfilled,
      (state: IUserActionsState, action: PayloadAction<TUniteUsersCSRF>) => {
        state.uniteUsersCSRF.isLoading = false;
        state.uniteUsersCSRF = action.payload;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error("Ошибка запроса uniteUsersCSRF", action.payload.error);
          state.uniteUsersCSRF.error = action.payload.error;
        }
      }
    );
  },
});

export default userActionsSlice.reducer;
